/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable react/no-danger */
import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from '@components/v2/Modal';
import { Button } from '@ui';
import Label from '@components/Label';
import InspectionStatusSelector from '@components/v2/InspectionStatusSelector';
import { translate } from '@utils/i18n';
import useIIMIssues from '@features/IIMReview/hooks/useIIMIssues';
import useAIInspectionManager from '@features/Settings/AiInspectionManager/hooks/useAIInspectionManager';
import { IssueStatusEnum, ModeIA } from '@features/IIMReview/types';
import { snakeToCapitalize } from '@features/DocumentsList/utils/utils';
import { useBulkUpdateIssues } from '@features/IIMReview/hooks/useUpdateIssue';
import { toast } from 'react-hot-toast';
import useCompleteReview from '../hooks/useCompleteReview';
import './styles.scss';
const namespace = 'features.ai_to_review_inspections';
function CompleteReviewModal({ isOpen, inspectionId, currentStatus = null, onClose }) {
    var _a, _b;
    const [selectedStatus, setSelectedStatus] = useState(currentStatus);
    const completeReviewMutation = useCompleteReview();
    const { data: settingsData, isLoading: isLoadingSettings } = useAIInspectionManager();
    const requireIssueResolutionEnabled = settingsData === null || settingsData === void 0 ? void 0 : settingsData.aiInspectionManagerRequireIssueResolutionEnabled;
    const unacceptableStates = [IssueStatusEnum.Detected, IssueStatusEnum.Suggested, IssueStatusEnum.Skipped];
    const { data: issuesData, isFetching: isLoadingIssues, isError: errorIssues } = useIIMIssues(Number(inspectionId), unacceptableStates, ModeIA.Full, {
        enabled: isOpen
    });
    const bulkUpdateMutation = useBulkUpdateIssues({
        inspectionId: Number(inspectionId),
        issueIds: ((_a = issuesData === null || issuesData === void 0 ? void 0 : issuesData.issues) === null || _a === void 0 ? void 0 : _a.map((issue) => issue.id)) || []
    });
    // Reset selectedStatus when modal opens or currentStatus changes
    useEffect(() => {
        setSelectedStatus(currentStatus);
    }, [currentStatus]);
    const handleStatusSelect = (status) => {
        setSelectedStatus((status === null || status === void 0 ? void 0 : status.value) || null);
    };
    const handleComplete = () => {
        if (!selectedStatus)
            return;
        completeReviewMutation.mutate({
            inspectionId,
            statusId: selectedStatus
        }, {
            onSuccess: () => {
                onClose();
            },
            onError: (error) => {
                toast.error(`Error updating inspection status: ${error instanceof Error ? error.message : 'Unknown error'}`);
            }
        });
    };
    const handleDismissAllAndComplete = () => {
        if (!selectedStatus)
            return;
        bulkUpdateMutation.mutate({
            status: IssueStatusEnum.DismissedByBackOffice
        }, {
            onSuccess: () => {
                handleComplete();
            },
            onError: () => {
                toast.error(translate('error_dismissing_all', { namespace }));
            }
        });
    };
    const handleClose = () => {
        setSelectedStatus(null);
        onClose();
    };
    const issuesGroupedByCategory = useMemo(() => {
        var _a;
        return (_a = issuesData === null || issuesData === void 0 ? void 0 : issuesData.issues) === null || _a === void 0 ? void 0 : _a.reduce((acc, issue) => {
            acc[issue.issueCategory] = (acc[issue.issueCategory] || 0) + 1;
            return acc;
        }, {});
    }, [issuesData === null || issuesData === void 0 ? void 0 : issuesData.issues]);
    const isBlocked = Object.values(issuesGroupedByCategory || {}).some((count) => count > 0);
    const isProcessing = completeReviewMutation.isLoading || bulkUpdateMutation.isLoading;
    if (isLoadingSettings) {
        return (React.createElement(Modal, { isOpen: isOpen, width: "560px", onClose: handleClose, title: translate('loading', { namespace }) },
            React.createElement("div", { className: "text-center" },
                React.createElement("i", { className: "fa-light fa-spinner-third fa-spin" }),
                translate('loading', { namespace }))));
    }
    if (isBlocked && requireIssueResolutionEnabled) {
        return (React.createElement(Modal, { isOpen: isOpen, width: "560px", onClose: handleClose, title: translate('inspection_cannot_be_completed', {
                namespace
            }), footer: React.createElement(React.Fragment, null,
                React.createElement(Button, { onClick: handleClose, variant: "secondary", disabled: isProcessing }, translate('close', { namespace: 'features.commons' })),
                React.createElement(Button, { onClick: () => {
                        void handleDismissAllAndComplete();
                    }, variant: "primary", disabled: !selectedStatus || isProcessing }, isProcessing ? (React.createElement("i", { className: "fa-light fa-spinner-third fa-spin" })) : (translate('dismiss_all', { namespace })))) },
            React.createElement("div", { className: "complete-review-modal__alert" },
                React.createElement("i", { className: "fa-light fa-circle-exclamation complete-review-modal__alert-icon" }),
                React.createElement("span", { dangerouslySetInnerHTML: {
                        __html: translate('unresolved_issues_warning_html', {
                            namespace,
                            count: ((_b = issuesData === null || issuesData === void 0 ? void 0 : issuesData.issues) === null || _b === void 0 ? void 0 : _b.length) || 0
                        })
                    } })),
            React.createElement("br", null),
            React.createElement("div", { className: "mb-3" },
                React.createElement("h6", null,
                    React.createElement("strong", null, translate('unresolved_issues', { namespace }))),
                React.createElement("div", { className: "complete-review-modal__category-container" }, Object.entries(issuesGroupedByCategory || {}).map(([category, count]) => (React.createElement("div", { key: category, className: "complete-review-modal__category-row" },
                    React.createElement("span", { className: "complete-review-modal__category-name" },
                        React.createElement("strong", null, snakeToCapitalize(category))),
                    React.createElement("span", { className: "complete-review-modal__issue-badge" },
                        count,
                        " ",
                        count === 1 ? 'Issue' : 'Issues')))))),
            React.createElement("div", { className: "mb-3" },
                React.createElement("p", { className: "mb-3" }, translate('complete_review_description', { namespace })),
                React.createElement("div", { className: "d-flex" },
                    React.createElement(Label, { htmlFor: "statusInput", className: "info-key" },
                        translate('status', { namespace: 'features.commons' }),
                        ":"),
                    React.createElement(InspectionStatusSelector, { id: "statusInput", showSearch: true, maxHeight: 280, allowClear: false, value: selectedStatus || null, onChange: handleStatusSelect })))));
    }
    return (React.createElement(Modal, { isOpen: isOpen, width: "560px", onClose: handleClose, title: translate('complete_review', { namespace }), footer: React.createElement(React.Fragment, null,
            React.createElement(Button, { onClick: handleClose, variant: "secondary", disabled: isProcessing }, translate('cancel', { namespace: 'features.commons' })),
            React.createElement(Button, { onClick: () => {
                    void handleComplete();
                }, variant: "primary", disabled: !selectedStatus || isProcessing || isLoadingIssues }, isProcessing ? (React.createElement("i", { className: "fa-light fa-spinner-third fa-spin" })) : (translate('complete', { namespace: 'features.commons' })))) },
        React.createElement("div", { className: "mb-4" },
            isLoadingIssues && (React.createElement("div", { className: "mb-3 text-muted" },
                React.createElement("i", { className: "fa-light fa-spinner-third fa-spin me-2" }),
                translate('checking_for_unresolved_issues', { namespace }))),
            errorIssues && (React.createElement("div", { className: "alert alert-warning mb-3", role: "alert" },
                React.createElement("i", { className: "fa-light fa-triangle-exclamation me-2" }),
                translate('unable_to_validate_review_status', { namespace }))),
            React.createElement("p", { className: "mb-3" }, translate('complete_review_description', { namespace })),
            React.createElement("div", { className: "d-flex" },
                React.createElement(Label, { htmlFor: "statusInput", className: "info-key" },
                    translate('status', { namespace: 'features.commons' }),
                    ":"),
                React.createElement(InspectionStatusSelector, { id: "statusInput", showSearch: true, maxHeight: 280, allowClear: false, value: selectedStatus || null, onChange: handleStatusSelect })))));
}
export default CompleteReviewModal;
